/**
 * @fileoverview Assigns or changes the owner of an accepted prospect.
 * @description Opened from a Sales Pipeline backlog row by a Super Admin, Sales Head or Sales
 * Manager. Offers the Sales Executives the viewer may assign (GET /sales/prospects/eligible-owners,
 * the same list as the New Prospect form; a Manager sees only their own team) and saves the choice
 * through Assign Prospect Owner (see useAssignProspectOwner). The backend rechecks everything, so
 * a refusal (no longer eligible, prospect changed or archived, recent sign-in needed) shows as the
 * dialog's error. Close is held while the request is in flight, and the confirmation runs from the
 * mutation itself (so it survives an unmount). The parent mounts it only while it's open, so every
 * opening starts clean.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Anshul Rokde
 */

import { zodResolver } from '@hookform/resolvers/zod'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { useAssignProspectOwner } from '@/features/sales/hooks/useAssignProspectOwner'
import { useEligibleProspectOwners } from '@/features/sales/hooks/useEligibleProspectOwners'
import { useProspectDetails } from '@/features/sales/hooks/useProspectDetails'
import { useRefreshSalesQueries } from '@/features/sales/hooks/useRefreshSalesQueries'
import { requestErrorMessage } from '@/features/sales/lib/request-error.utils'
import { isStaleVersionError } from '@/features/sales/lib/stale-version.utils'
import { assignOwnerFormSchema } from '@/features/sales/schemas/assign-owner-form.schema'
import Modal from '@/shared/components/molecules/Modal'
import SelectField from '@/shared/components/molecules/SelectField'
import { useGuardedClose } from '@/shared/hooks/useGuardedClose'

import DialogActions from './DialogActions'
import SalesRequestError from './SalesRequestError'
import StaleVersionNotice from './StaleVersionNotice'

import type { AssignOwnerFormValues } from '@/features/sales/schemas/assign-owner-form.schema'
import type {
  ProspectListItem,
  ProspectOwnerAssignment,
} from '@/features/sales/types/prospect.types'
import type { FormEvent } from 'react'

interface AssignOwnerDialogProps {
  prospect: ProspectListItem
  onClose: () => void
  onAssigned: (result: ProspectOwnerAssignment, prospect: ProspectListItem) => void
}

/**
 * @component
 * @param {AssignOwnerDialogProps} props - The prospect and the handlers.
 * @param {ProspectListItem} props.prospect - The accepted prospect to assign.
 * @param {() => void} props.onClose - Called when the dialog is dismissed.
 * @param {(result: ProspectOwnerAssignment, prospect: ProspectListItem) => void} props.onAssigned -
 * Called once the owner is saved.
 */
function AssignOwnerDialog({ prospect, onClose, onAssigned }: AssignOwnerDialogProps) {
  const { t } = useTranslation()
  const ownersQuery = useEligibleProspectOwners(true)
  const details = useProspectDetails(prospect.prospectId)
  const assign = useAssignProspectOwner({
    /**
     * @description Reports the saved owner to the page, even if this dialog has unmounted.
     * @param {ProspectOwnerAssignment} result - The prospect's owner now.
     */
    onSuccess: (result) => onAssigned(result, prospect),
  })
  const close = useGuardedClose(onClose, assign.isPending)
  const refreshSales = useRefreshSalesQueries()
  const owners = ownersQuery.data ?? []
  const loaded = details.data
  const currentOwner = loaded ? loaded.prospect.currentOwner : prospect.currentOwner
  const isStale = assign.isError && isStaleVersionError(assign.error)
  const canSubmit =
    ownersQuery.isSuccess && owners.length > 0 && details.isSuccess && !details.isFetching
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<AssignOwnerFormValues>({
    resolver: zodResolver(assignOwnerFormSchema),
    defaultValues: { ownerUserId: '' },
  })

  /**
   * @description Saves the validated owner against the version shown; the mutation reports it.
   * @param {AssignOwnerFormValues} values - The form's values.
   */
  const onValid = (values: AssignOwnerFormValues) => {
    if (!loaded) {
      return
    }
    assign.mutate({
      prospectId: prospect.prospectId,
      etag: loaded.etag,
      ownerUserId: values.ownerUserId,
    })
  }

  /**
   * @description Clears a stale refusal and reloads the prospect's latest state for review.
   */
  const onRefresh = () => {
    assign.reset()
    refreshSales()
  }

  /**
   * @description Validates and submits the form.
   * @param {FormEvent<HTMLFormElement>} event - The submit event.
   */
  const onSubmit = (event: FormEvent<HTMLFormElement>) => {
    void handleSubmit(onValid)(event)
  }

  return (
    <Modal
      open
      onClose={close}
      data-testid="assign-owner-dialog"
      title={t('sales.pipeline.assignOwner.title')}
      subtitle={prospect.bankName}
    >
      <form
        onSubmit={onSubmit}
        noValidate
        className="flex flex-col gap-4"
        data-testid="assign-owner-form"
      >
        <p className="text-[13.5px] text-ink" data-testid="assign-owner-current">
          {currentOwner
            ? t('sales.pipeline.assignOwner.current', { name: currentOwner.displayName })
            : t('sales.pipeline.assignOwner.unassigned')}
        </p>
        <SelectField
          id="assignOwner"
          data-testid="assign-owner-select"
          label={t('sales.pipeline.assignOwner.ownerLabel')}
          disabled={!ownersQuery.isSuccess || owners.length === 0}
          options={[
            { value: '', label: t('sales.pipeline.form.selectPlaceholder') },
            ...owners.map((owner) => ({
              value: owner.userId,
              label: owner.employeeCode
                ? `${owner.displayName} (${owner.employeeCode})`
                : owner.displayName,
            })),
          ]}
          error={errors.ownerUserId?.message ? t(errors.ownerUserId.message) : undefined}
          {...register('ownerUserId')}
        />
        {ownersQuery.isSuccess && owners.length === 0 && (
          <p className="text-[12px] text-slate" data-testid="assign-owner-no-executives-message">
            {t('sales.pipeline.create.fields.executiveNone')}
          </p>
        )}
        {ownersQuery.isError && <SalesRequestError error={ownersQuery.error} />}
        {details.isError && (
          <p role="alert" className="text-[13px] text-danger" data-testid="assign-owner-load-error">
            {requestErrorMessage(details.error, t)}
          </p>
        )}
        {isStale && <StaleVersionNotice onRefresh={onRefresh} isRefreshing={details.isFetching} />}
        {assign.isError && !isStale && <SalesRequestError error={assign.error} />}
        <DialogActions
          testIdPrefix="assign-owner"
          cancelLabel={t('sales.pipeline.approve.cancel')}
          confirmLabel={t('sales.pipeline.assignOwner.submit')}
          onCancel={close}
          confirmType="submit"
          isLoading={assign.isPending}
          disabled={!canSubmit || isStale}
        />
      </form>
    </Modal>
  )
}

export default AssignOwnerDialog
